import React from "react";
import { Menu, MenuItem, Box, Typography, Grid, Divider } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { STORY_CATEGORIES } from "../StoryForm/StoryFormConstants";
import { formatCategoryName } from "../../utility/category.utils";
import { getGlassCardStyles } from "../../utility/glassCard.utils";
import { SmallTextGradient, IconGradient } from "../Gradient";
import ExploreIcon from "@mui/icons-material/Explore";
import type { StoryCategory } from "../../types/models";

type ExploreMenuProps = {
  exploreMenuAnchor: HTMLElement | null;
  exploreMenuOpen: boolean;
  handleExploreMenuClose: () => void;
  setActive: (key: string) => void;
};

// Group categories for 3-column layout
const CATEGORY_GROUPS = {
  Fiction: [
    "FanFiction",
    "ScienceFiction",
    "Fantasy",
    "Romance",
    "Mystery",
    "Thriller",
    "Horror",
    "Historical",
    "Action",
    "Adventure",
    "Comedy",
    "Drama",
    "ChickLit",
    "YoungAdult",
    "ShortStory",
    "GeneralFiction",
  ],
  "Non-Fiction": [
    "PersonalGrowth",
    "MentalHealth",
    "Career",
    "Education",
    "HealthAndFitness",
    "Lifestyle",
    "Finance",
    "Food",
    "Travel",
    "Tech",
    "Religion",
    "Culture",
    "BooksAndReviews",
    "CollegeLife",
    "NonFiction",
  ],
  Other: [
    "Poetry",
    "Paranormal",
    "Urban",
    "Western",
    "Other",
  ],
};

const ExploreMenu: React.FC<ExploreMenuProps> = ({
  exploreMenuAnchor,
  exploreMenuOpen,
  handleExploreMenuClose,
  setActive,
}) => {
  const navigate = useNavigate();

  const handleExploreAll = () => {
    handleExploreMenuClose();
    setActive("explore");
    navigate("/explore");
  };

  const handleCategoryClick = (category: StoryCategory) => {
    handleExploreMenuClose();
    setActive("explore");
    navigate(`/explore?category=${category}`);
  };

  return (
    <Menu
      anchorEl={exploreMenuAnchor}
      open={exploreMenuOpen}
      onClose={handleExploreMenuClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            elevation: 0,
            sx: (theme) => ({
              ...getGlassCardStyles(theme),
              mt: 1,
              p: { xs: 1.5, sm: 2 },
              width: "auto",
              minWidth: { xs: "90vw", sm: 600 },
              maxWidth: { xs: "95vw", sm: 700 },
              maxHeight: { xs: "75vh", sm: "80vh" },
              borderRadius: 3,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.05)",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
            borderRadius: "4px",
            "&:hover": {
              background: theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(0, 0, 0, 0.3)",
            },
          },
        }),
      }}
    >
      {/* Explore All Option */}
      <MenuItem
        component={Link}
        to="/explore"
        onClick={handleExploreAll}
        sx={(theme) => ({
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1, sm: 1.25 },
          backgroundColor: "transparent",
          display: "flex",
          alignItems: "center",
          gap: 1,
          transition: "all 0.2s ease",
          borderRadius: 1,
          mx: 1,
          my: 0.5,
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.04)",
          },
        })}
      >
        <IconGradient gradientVariant="default">
          <ExploreIcon fontSize="small" />
        </IconGradient>
        <SmallTextGradient
          variant="body2"
          gradientVariant="default"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          }}
        >
          Explore All
        </SmallTextGradient>
      </MenuItem>

      <Divider
        sx={{
          borderColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          mx: 1,
          my: 0.5,
        }}
      />

      {/* 3 Column Layout */}
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: { xs: 1, sm: 1.5 } }}>
        <Grid container spacing={2} sx={{ width: "100%", margin: 0 }}>
        {Object.entries(CATEGORY_GROUPS).map(([groupName, categories]) => (
          <Grid item xs={12} sm={4} key={groupName}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {/* Column Header */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                  mb: 1.5,
                  color: "text.primary",
                }}
              >
                <SmallTextGradient gradientVariant="default">
                  {groupName}
                </SmallTextGradient>
              </Typography>

              {/* Categories List */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                {categories
                  .filter((cat) => STORY_CATEGORIES.includes(cat as StoryCategory))
                  .map((category) => (
                    <MenuItem
                      key={category}
                      onClick={() => handleCategoryClick(category as StoryCategory)}
                      sx={(theme) => ({
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 0.75, sm: 0.875 },
                        borderRadius: 1,
                        minHeight: { xs: 36, sm: 40 },
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.04)",
                        },
                      })}
                    >
                      <SmallTextGradient
                        variant="body2"
                        gradientVariant="subtle"
                        sx={{
                          fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                          fontWeight: 400,
                        }}
                      >
                        {formatCategoryName(category as StoryCategory)}
                      </SmallTextGradient>
                    </MenuItem>
                  ))}
              </Box>
            </Box>
          </Grid>
        ))}
        </Grid>
      </Box>
    </Menu>
  );
};

export default ExploreMenu;

